import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Youtube, Instagram, Linkedin } from "lucide-react";
import { CONTACT, SOCIALS } from "@/data/content";

const QUICK_LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About Us" },
  { to: "/products", label: "Products" },
  { to: "/solutions", label: "Hospitality Solutions" },
  { to: "/customers", label: "Our Customers" },
  { to: "/contact", label: "Contact" },
];

const CATEGORIES = ["Bed Linen", "Bath Linen", "Towels", "Pillows", "Mattresses", "Hospitality Essentials"];

const Footer = () => (
  <footer data-testid="site-footer" className="bg-ink text-ivory">
    <div className="max-w-[1400px] mx-auto px-6 md:px-12 pt-20 pb-10">
      <div className="grid md:grid-cols-12 gap-12 md:gap-8">
        <div className="md:col-span-4">
          <p className="font-serif text-4xl font-medium tracking-tight">
            Amazing <span className="italic text-gold">Grace</span>
          </p>
          <p className="text-[10px] tracking-[0.32em] uppercase text-ivory/50 mt-2">
            Hospitality Textiles & Hotel Essentials
          </p>
          <p className="text-ivory/60 text-sm leading-relaxed mt-6 max-w-xs">
            Manufacturer and supplier of premium bedding, bath textiles and hospitality essentials for hotels, resorts and institutions.
          </p>
        </div>

        <div className="md:col-span-2">
          <p className="text-[11px] tracking-[0.28em] uppercase text-gold mb-6">Quick Links</p>
          <ul className="space-y-3">
            {QUICK_LINKS.map((l) => (
              <li key={l.to}>
                <Link
                  to={l.to}
                  data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="text-sm text-ivory/70 hover:text-gold transition-colors duration-300"
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-3">
          <p className="text-[11px] tracking-[0.28em] uppercase text-gold mb-6">Product Categories</p>
          <ul className="space-y-3">
            {CATEGORIES.map((c) => (
              <li key={c}>
                <Link to="/products" className="text-sm text-ivory/70 hover:text-gold transition-colors duration-300">
                  {c}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-3">
          <p className="text-[11px] tracking-[0.28em] uppercase text-gold mb-6">Contact</p>
          <ul className="space-y-4 text-sm text-ivory/70">
            <li className="flex gap-3">
              <MapPin size={16} className="text-gold shrink-0 mt-0.5" />
              <span>26 Mookambikai Nagar, Teachers Colony, Karamadai, Coimbatore, Tamil Nadu, India - 641104</span>
            </li>
            <li className="flex gap-3">
              <Phone size={16} className="text-gold shrink-0 mt-0.5" />
              <span className="space-y-1">
                {CONTACT.phones.map((p) => (
                  <a
                    key={p}
                    href={`tel:${p.replace(/\s/g, "")}`}
                    data-testid={`footer-phone-${p.replace(/[^0-9]/g, "")}`}
                    className="block hover:text-gold transition-colors duration-300"
                  >
                    {p}
                  </a>
                ))}
              </span>
            </li>
            <li className="flex gap-3">
              <Mail size={16} className="text-gold shrink-0 mt-0.5" />
              <a href={`mailto:${CONTACT.email}`} data-testid="footer-email-link" className="hover:text-gold transition-colors duration-300 break-all">
                {CONTACT.email}
              </a>
            </li>
            <li className="text-xs text-ivory/40 pt-2">
              GSTIN: {CONTACT.gstin} &nbsp;·&nbsp; PAN: {CONTACT.pan}
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-ivory/10 mt-16 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
        <p data-testid="footer-copyright" className="text-xs text-ivory/40 tracking-wide">
          © 2026 Amazing Grace. All Rights Reserved.
        </p>
        <div className="flex items-center gap-3">
          {[
            { href: SOCIALS.youtube, label: "YouTube", Icon: Youtube },
            { href: SOCIALS.instagram, label: "Instagram", Icon: Instagram },
            { href: SOCIALS.linkedin, label: "LinkedIn", Icon: Linkedin },
          ].map(({ href, label, Icon }) => (
            <a
              key={label}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={label}
              data-testid={`footer-social-${label.toLowerCase()}`}
              className="w-9 h-9 flex items-center justify-center border border-ivory/20 text-ivory/60 hover:text-gold hover:border-gold transition-colors duration-300"
            >
              <Icon size={16} />
            </a>
          ))}
        </div>
        <p className="text-xs text-ivory/40 tracking-[0.2em] uppercase">
          Quality Hospitality Textiles & Hotel Essentials
        </p>
      </div>
    </div>
  </footer>
);

export default Footer;
